import { useEffect, useState, type ReactNode } from 'react';
import { Play } from 'lucide-react';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Textarea,
  playOnLudolio,
  toast,
} from '@ludolio/shared-ui';
import { useLabsReview } from './context';
import { eventLabel, formatBytes, formatDate, platformFamily, visibilityLabel } from './format';
import { ProjectQuizzes } from './ProjectQuizzes';
import type { LabManagedQuiz, LabReviewDecision, LabReviewRelease } from './types';

function DetailRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[8rem_minmax(0,1fr)] gap-4 py-2 text-sm">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="min-w-0">{children}</dd>
    </div>
  );
}

export function LabsReviewDetail({
  releaseId,
  onClose,
  onDecided,
  canReadResponses = false,
}: {
  releaseId: string | null;
  onClose: () => void;
  onDecided?: () => void;
  canReadResponses?: boolean;
}) {
  const { api, getErrorMessage } = useLabsReview();
  const [detail, setDetail] = useState<LabReviewRelease | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [quizzes, setQuizzes] = useState<LabManagedQuiz[] | null>(null);
  const [quizError, setQuizError] = useState('');

  useEffect(() => {
    if (!releaseId) return;
    let active = true;
    setDetail(null);
    setQuizzes(null);
    setQuizError('');
    setNote('');
    setLoadError('');
    api.getRelease(releaseId)
      .then((loaded) => {
        if (!active) return;
        setDetail(loaded);
        api.listQuizzes(loaded.project.id)
          .then((result) => active && setQuizzes(result ?? []))
          .catch((err) => active && setQuizError(getErrorMessage(err)));
      })
      .catch((err) => active && setLoadError(getErrorMessage(err)));
    return () => {
      active = false;
    };
  }, [api, getErrorMessage, releaseId]);

  const decide = async (action: LabReviewDecision) => {
    if (!releaseId) return;
    setBusy(true);
    try {
      setDetail(await api.decide(releaseId, action, note.trim() || undefined));
      toast.success(action === 'approve' ? 'Approved and published' : 'Changes requested');
      onDecided?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const release = detail?.release;
  const appId = detail?.project.appId;

  return (
    <Dialog open={Boolean(releaseId)} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{release ? `${release.title} - ${release.version}` : 'Review'}</DialogTitle>
        </DialogHeader>

        {loadError && <p className="text-sm text-destructive" role="alert">{loadError}</p>}
        {!detail && !loadError && <p className="text-sm text-muted-foreground">Loading…</p>}

        {detail && release && (
          <div className="space-y-6">
            {release.bannerUrl && (
              <img src={release.bannerUrl} alt="" className="aspect-video w-full rounded-lg border object-cover" />
            )}

            <dl className="divide-y">
              <DetailRow label="Unit">{detail.project.unit ? `${detail.project.unit.code} - ${detail.project.unit.name}` : 'No unit'}</DetailRow>
              <DetailRow label="Who can play">{visibilityLabel(release.visibility)}</DetailRow>
              <DetailRow label="Submitted">{formatDate(release.submittedAt) || 'Not submitted'}</DetailRow>
              <DetailRow label="Team">
                {detail.members.map((member) => `${member.name} (${member.email})`).join(', ')}
              </DetailRow>
              <DetailRow label="Credits">
                {release.credits.length > 0
                  ? release.credits.map((credit) => `${credit.name}, ${credit.contribution}`).join('; ')
                  : 'None'}
              </DetailRow>
              <DetailRow label="Content notes">{release.contentNotes || 'None'}</DetailRow>
              <DetailRow label="Remix">
                {detail.remix.permission === 'organization'
                  ? `University${detail.remix.termsVersion ? ` | terms ${detail.remix.termsVersion}` : ''}`
                  : 'Not remixable'}
              </DetailRow>
              <DetailRow label="Assets">
                {detail.remix.assetSharingConfirmed ? 'Sharing confirmed' : 'Not confirmed'}
              </DetailRow>
              {detail.remix.structureConfirmed !== null && (
                <DetailRow label="Structure">
                  {detail.remix.structureConfirmed ? 'Confirmed' : 'Needs confirmation'}
                </DetailRow>
              )}
              {detail.lineage.ancestors.length > 0 && (
                <DetailRow label="Original">
                  {detail.lineage.ancestors.map((ancestor) => (
                    <span key={ancestor.releaseId} className="block">
                      {ancestor.title} - {ancestor.version}
                      {ancestor.withdrawn ? ' (taken down)' : ''}
                    </span>
                  ))}
                </DetailRow>
              )}
            </dl>

            <div className="space-y-2">
              <h3 className="text-sm font-semibold">Description</h3>
              <p className="whitespace-pre-line text-sm">{release.description || 'No description'}</p>
            </div>

            <ProjectQuizzes quizzes={quizzes} error={quizError} canReadResponses={canReadResponses} />

            <div className="space-y-2">
              <h3 className="text-sm font-semibold">Builds</h3>
              <ul className="divide-y rounded-lg border">
                {release.builds.map((build) => (
                  <li key={build.id} className="grid grid-cols-[7rem_minmax(0,1fr)_5rem] items-center gap-4 p-3 text-sm">
                    <span className="font-medium">{platformFamily(build.platform)}</span>
                    <span className="truncate text-muted-foreground" title={build.executablePath ?? undefined}>{build.executablePath}</span>
                    <span className="text-right text-muted-foreground">{formatBytes(build.fileSize)}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="space-y-2">
              <h3 className="text-sm font-semibold">Source project</h3>
              {release.sourcePackage ? (
                <div className="grid grid-cols-[7rem_minmax(0,1fr)_5rem] items-center gap-4 rounded-lg border p-3 text-sm">
                  <span className="font-medium capitalize">{release.sourcePackage.engine}</span>
                  <span className="truncate text-muted-foreground">
                    {release.sourcePackage.fileCount.toLocaleString()} files
                    {release.sourcePackage.engineVersion ? ` | ${release.sourcePackage.engineVersion}` : ''}
                  </span>
                  <span className="text-right text-muted-foreground">
                    {formatBytes(release.sourcePackage.fileSize)}
                  </span>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No source package attached</p>
              )}
              {release.sourcePackage?.status === 'passed' && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={busy}
                  onClick={() => {
                    setBusy(true);
                    api.downloadSource(release.id)
                      .then((result) => window.location.assign(result.url))
                      .catch((err) => toast.error(getErrorMessage(err)))
                      .finally(() => setBusy(false));
                  }}
                >
                  Download source
                </Button>
              )}
            </div>

            {detail.events.length > 0 && (
              <div className="space-y-2">
                <h3 className="text-sm font-semibold">Activity</h3>
                <ul className="space-y-2 text-sm">
                  {detail.events.map((event, index) => (
                    <li key={index} className="grid grid-cols-[8rem_minmax(0,1fr)] gap-4">
                      <span className="text-muted-foreground">{formatDate(event.createdAt)}</span>
                      <span>
                        <Badge variant="secondary" className="mr-2">{eventLabel(event.action)}</Badge>
                        {event.actor}
                        {event.note && <span className="mt-1 block whitespace-pre-line text-muted-foreground">{event.note}</span>}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {detail.canDecide && (
              <div className="space-y-2">
                <Label htmlFor="review-note">Note to the team</Label>
                <Textarea
                  id="review-note"
                  rows={3}
                  maxLength={2000}
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                  placeholder="Required when requesting changes"
                  disabled={busy}
                />
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2 sm:justify-between">
          <Button variant="outline" disabled={!appId} onClick={() => appId && playOnLudolio(appId)}>
            <Play />
            Play in Ludolio
          </Button>
          {detail?.canDecide && (
            <div className="flex flex-col-reverse gap-2 sm:flex-row">
              <Button variant="outline" disabled={busy || !note.trim()} onClick={() => void decide('request_changes')}>
                Request changes
              </Button>
              <Button disabled={busy} onClick={() => void decide('approve')}>Approve and publish</Button>
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
